import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Row,
  Col,
  Spin,
  Table,
  Tag,
  Descriptions,
  Modal,
  message,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FilePptOutlined,
  FilePdfOutlined,
  FileOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { mekakApi } from './services';
import type { FileDocumentDetail as FileDocDetailType } from './models';
import { formatDateShort, formatCostUSD } from './services';
import ImageGallery from './components/ImageGallery';

const { Text } = Typography;

const FileDocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<FileDocDetailType | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await mekakApi.getFileDocumentDetail(id);
      if (res.data?.success) {
        setDetail(res.data.data);
      } else {
        message.error('檔案不存在');
        navigate('/mekak');
      }
    } catch {
      message.error('載入失敗');
      navigate('/mekak');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleDelete = () => {
    if (!id) return;
    Modal.confirm({
      title: '確認刪除',
      content: '刪除此檔案及其所有知識點？此操作無法復原。',
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await mekakApi.deleteFileDocument(id);
          message.success('已刪除');
          navigate('/mekak');
        } catch {
          message.error('刪除失敗');
        }
      },
    });
  };

  const fileIcon = (type: string) => {
    if (type === 'pptx') return <FilePptOutlined style={{ fontSize: 24, color: '#d4380d' }} />;
    if (type === 'pdf') return <FilePdfOutlined style={{ fontSize: 24, color: '#cf1322' }} />;
    return <FileOutlined style={{ fontSize: 24 }} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64 py-16">
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  if (!detail) return null;

  const { file_document: doc, knowledge, images = [] } = detail;

  const columns: ColumnsType<(typeof knowledge)[number]> = [
    {
      title: '標題',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string, record) => (
        <Button
          type="link"
          style={{ padding: 0, textAlign: 'left', whiteSpace: 'normal', height: 'auto' }}
          onClick={() => navigate(`/mekak/knowledge/${record.id}`)}
        >
          {title}
        </Button>
      ),
    },
    {
      title: '頁碼',
      dataIndex: 'page_numbers',
      key: 'page_numbers',
      width: 120,
      render: (pages: number[]) =>
        pages && pages.length > 0 ? (
          <Space size={2}>
            {pages.map((p) => (
              <Tag key={p} color="blue">p.{p}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '建立日期',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateShort(date)}
        </Text>
      ),
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Space align="center">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/mekak')}
                type="text"
                size="large"
              />
              {fileIcon(doc.file_type)}
              <h2 className="m-0 text-xl font-semibold">{doc.original_filename}</h2>
              <Tag color={doc.processing_status === 'completed' ? 'green' : 'orange'}>
                {doc.processing_status === 'completed' ? '已完成' : doc.processing_status}
              </Tag>
            </Space>
          </Col>
          <Col>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              刪除檔案
            </Button>
          </Col>
        </Row>
      </Card>

      {/* File metadata */}
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="檔案類型">
            {doc.file_type.toUpperCase()}
          </Descriptions.Item>
          <Descriptions.Item label="檔案大小">
            {(doc.file_size_bytes / 1024).toFixed(1)} KB
          </Descriptions.Item>
          <Descriptions.Item label="頁數">
            {doc.page_count}
          </Descriptions.Item>
          <Descriptions.Item label="知識點數量">
            {doc.knowledge_count}
          </Descriptions.Item>
          <Descriptions.Item label="成本">
            {doc.total_cost > 0 ? formatCostUSD(doc.total_cost) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="上傳日期">
            {formatDateShort(doc.created_at)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Images gallery */}
      {images.length > 0 && (
        <Card
          title={`圖片 (${images.length})`}
          className="mb-4"
          style={{ borderRadius: 8 }}
        >
          <ImageGallery images={images} editable onImageUpdated={fetchDetail} />
        </Card>
      )}

      {/* Knowledge entries table */}
      <Card
        title={`知識點 (${knowledge.length})`}
        style={{ borderRadius: 8 }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={knowledge}
          pagination={false}
          locale={{ emptyText: '此檔案尚未產生知識點' }}
        />
      </Card>
    </div>
  );
};

export default FileDocumentDetail;
